import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect, useMemo } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetWorkouts, useUpdateWorkout, useDeleteWorkout } from "../../api/hooks/useWorkout";
import { showError, showSuccess } from "../../helpers/toast";
import Pagination from "../../components/Panigation";
import { DIFFICULTY_LEVEL, WORKOUT_DURATION } from "../../constants/settings";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const WORKOUT_PAGE_LIMIT_OPTIONS = [10, 25, 50, 100] as const;

const ManageWorkout = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [workoutToDelete, setWorkoutToDelete] = useState<any | null>(null);
    const [sortConfig, setSortConfig] = useState<{
        key: "title" | "refCode" | "duration" | "difficultyLevel" | null;
        direction: "asc" | "desc";
    }>({
        key: null,
        direction: "asc",
    });

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setPage(1);
        }, 500);
        return () => clearTimeout(timerId);
    }, [searchTerm]);

    const { data: workoutsData, isLoading, error: workoutsError, refetch } = useGetWorkouts({
        search: debouncedSearchTerm || undefined,
        page,
        limit,
        offset: (page - 1) * limit,
    });

    const { workouts, totalCount } = useMemo(() => {
        const raw = workoutsData?.data;
        let list: any[] = [];
        let total = 0;
        if (Array.isArray(raw)) {
            list = raw;
            total = raw.length;
        } else if (raw && typeof raw === "object") {
            if (Array.isArray((raw as any).list)) {
                list = (raw as any).list;
                total = (raw as any).totalCount || list.length;
            }
        }
        return {
            workouts: Array.isArray(list) ? list : [],
            totalCount: total || 0,
        };
    }, [workoutsData]);

    const { mutate: deleteWorkout, isPending: isDeleting } = useDeleteWorkout({
        onSuccess: () => {
            showSuccess("Workout deleted");
            setDeleteDialogOpen(false);
            setWorkoutToDelete(null);
            refetch();
        },
        onError: (err: any) => {
            showError(err?.response?.data?.message || "Failed to delete workout");
        },
    });

    const sortedWorkouts = useMemo(() => {
        if (!sortConfig.key) return workouts;

        const sorted = [...workouts].sort((a: any, b: any) => {
            const { key } = sortConfig;

            if (key === "title") {
                const aVal = (a.title || "").toString().toLowerCase();
                const bVal = (b.title || "").toString().toLowerCase();
                return aVal.localeCompare(bVal);
            }

            if (key === "refCode") {
                const aVal = (a.refCode || "").toString().toLowerCase();
                const bVal = (b.refCode || "").toString().toLowerCase();
                return aVal.localeCompare(bVal);
            }

            if (key === "duration") {
                // Backend stores duration in `durationInMins` as seconds.
                const aVal = Number(a.durationInMins ?? a.duration) || 0;
                const bVal = Number(b.durationInMins ?? b.duration) || 0;
                return aVal - bVal;
            }

            if (key === "difficultyLevel") {
                const toDifficultyCodes = (v: any) => {
                    if (Array.isArray(v)) return v.map((x) => Number(x)).filter(Number.isFinite);
                    const n = Number(v);
                    return Number.isFinite(n) ? [n] : [];
                };
                const aCodes = toDifficultyCodes(a.difficultyLevel);
                const bCodes = toDifficultyCodes(b.difficultyLevel);
                const aVal = aCodes.length ? Math.max(...aCodes) : 0;
                const bVal = bCodes.length ? Math.max(...bCodes) : 0;
                return aVal - bVal;
            }

            return 0;
        });

        return sortConfig.direction === "asc" ? sorted : sorted.reverse();
    }, [workouts, sortConfig]);

    const handleSort = (key: "title" | "refCode" | "duration" | "difficultyLevel") => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return {
                key,
                direction: "asc",
            };
        });
    };

    useEffect(() => {
        if (workoutsError) {
            showError((workoutsError as any)?.response?.data?.message || "Failed to load workouts");
        }
    }, [workoutsError]);

    const formatDurationFromEnum = (val?: number) => {
        if (val === WORKOUT_DURATION.FIVE_MINS) return "5 Minutes";
        if (val === WORKOUT_DURATION.TEN_MINS) return "10 Minutes";
        if (val === WORKOUT_DURATION.TWENTY_MINS) return "20 Minutes";
        if (val === WORKOUT_DURATION.THIRTY_MINS) return "30 Minutes";
        if (val === WORKOUT_DURATION.FORTY_MINS) return "40 Minutes";
        if (val === WORKOUT_DURATION.SIXTY_MINS) return "60 Minutes";
        return "N/A";
    };

    const formatDurationShortFromEnum = (val?: number) => {
        if (val === WORKOUT_DURATION.FIVE_MINS) return "5m";
        if (val === WORKOUT_DURATION.TEN_MINS) return "10m";
        if (val === WORKOUT_DURATION.TWENTY_MINS) return "20m";
        if (val === WORKOUT_DURATION.THIRTY_MINS) return "30m";
        if (val === WORKOUT_DURATION.FORTY_MINS) return "40m";
        if (val === WORKOUT_DURATION.SIXTY_MINS) return "60m";
        return "—";
    };

    const formatDurationFromSeconds = (seconds?: number) => {
        const totalSecs = Number(seconds);
        if (!Number.isFinite(totalSecs)) return "—";

        const safeTotal = Math.max(0, Math.floor(totalSecs));
        const mins = Math.floor(safeTotal / 60);
        const secs = safeTotal % 60;
        return `${mins} m ${secs} s`;
    };

    const formatDurationShortFromSeconds = (seconds?: number) => {
        const totalSecs = Number(seconds);
        if (!Number.isFinite(totalSecs)) return "—";

        const safeTotal = Math.max(0, Math.floor(totalSecs));
        const mins = Math.floor(safeTotal / 60);
        const secs = safeTotal % 60;
        return `${mins}m ${secs}s`;
    };

    const formatDuration = (durationInMins?: number, durationEnum?: number) => {
        if (durationInMins !== undefined && durationInMins !== null && Number.isFinite(Number(durationInMins))) {
            return formatDurationFromSeconds(durationInMins);
        }
        return formatDurationFromEnum(durationEnum);
    };

    const formatDurationShort = (durationInMins?: number, durationEnum?: number) => {
        if (durationInMins !== undefined && durationInMins !== null && Number.isFinite(Number(durationInMins))) {
            return formatDurationShortFromSeconds(durationInMins);
        }
        return formatDurationShortFromEnum(durationEnum);
    };

    const formatDifficulty = (val?: number | number[]) => {
        const toCodes = (v: any) => {
            if (Array.isArray(v)) return v.map((x) => Number(x)).filter(Number.isFinite);
            const n = Number(v);
            return Number.isFinite(n) ? [n] : [];
        };

        const codes = toCodes(val);
        if (!codes.length) return "N/A";

        const labels = codes
            .map((code) => {
                if (code === DIFFICULTY_LEVEL.BEGGINER) return "Beginner";
                if (code === DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE) return "Beginner-Intermediate";
                if (code === DIFFICULTY_LEVEL.INTERMEDIATE) return "Intermediate";
                if (code === DIFFICULTY_LEVEL.ADVANCED) return "Advanced";
                return null;
            })
            .filter(Boolean) as string[];

        return labels.length ? labels.join(", ") : "N/A";
    };

    const getMainProgramTitle = (item: any) => {
        if (item?.mainProgram?.title) return item.mainProgram.title;
        if (item?.mainProgramId?.title) return item.mainProgramId.title;
        if (Array.isArray(item?.programs) && item.programs[0]?.title) return item.programs[0].title;
        return "N/A";
    };

    const { mutate: updateWorkoutStatus, isPending: isUpdatingStatus } = useUpdateWorkout({
        onSuccess: () => {
            showSuccess("Status updated");
            refetch();
        },
        onError: (err: any) => {
            showError(err?.response?.data?.message || "Failed to update status");
        },
    });

    const handleTogglePublish = (item: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
        updateWorkoutStatus({
            id: item._id,
            data: {
                isPublished: !item.isPublished,
            } as any,
        });
    };

    const handleLimitChange = (event: SelectChangeEvent<number>) => {
        const value = Number(event.target.value);
        if (!Number.isFinite(value) || value < 1) return;
        setLimit(value);
        setPage(1);
    };

    return (
        <>
            <div className="main_title">
                <h1>Manage Workouts</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Workouts
                </p>
            </div>

            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <SearchBar
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={setSearchTerm}
                            placeholder="Search Workout"
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            onCross={() => {
                                setSearchTerm("");
                                setDebouncedSearchTerm("");
                            }}
                        />
                    </div>

                    <div className="right_s" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="workout-list-limit-label">Per page</InputLabel>
                            <Select<number>
                                labelId="workout-list-limit-label"
                                id="workout-list-limit"
                                label="Per page"
                                value={limit}
                                onChange={handleLimitChange}
                            >
                                {WORKOUT_PAGE_LIMIT_OPTIONS.map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button className="btn btn_primary" onClick={() => navigate("add")}>
                            <AddIcon /> Add Workout
                        </Button>
                    </div>
                </div>

                <TableContainer className="table_container">
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : workoutsError ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <p>Error loading workouts.</p>
                            <Button onClick={() => refetch()} style={{ marginLeft: 10 }}>
                                Retry
                            </Button>
                        </Box>
                    ) : workouts.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <p>No workouts found.</p>
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Thumbnail</TableCell>
                                    <TableCell
                                        onClick={() => handleSort("title")}
                                        style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    >
                                        <b style={{ marginRight: 4 }}>
                                            {sortConfig.key === "title"
                                                ? sortConfig.direction === "asc"
                                                    ? "↑"
                                                    : "↓"
                                                : "↕"}
                                        </b>
                                        <span>Workout Title</span>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort("refCode")}
                                        style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    >
                                        <b style={{ marginRight: 4 }}>
                                            {sortConfig.key === "refCode"
                                                ? sortConfig.direction === "asc"
                                                    ? "↑"
                                                    : "↓"
                                                : "↕"}
                                        </b>
                                        <span>Ref Code</span>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort("duration")}
                                        style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    >
                                        <b style={{ marginRight: 4 }}>
                                            {sortConfig.key === "duration"
                                                ? sortConfig.direction === "asc"
                                                    ? "↑"
                                                    : "↓"
                                                : "↕"}
                                        </b>
                                        <span>Duration</span>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort("difficultyLevel")}
                                        style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    >
                                        <b style={{ marginRight: 4 }}>
                                            {sortConfig.key === "difficultyLevel"
                                                ? sortConfig.direction === "asc"
                                                    ? "↑"
                                                    : "↓"
                                                : "↕"}
                                        </b>
                                        <span>Level</span>
                                    </TableCell>
                                    <TableCell>Main Program</TableCell>
                                    <TableCell>Published</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {sortedWorkouts.map((item: any) => (
                                    <TableRow hover key={item._id}>
                                        <TableCell>
                                            <Box
                                                className="thumb_small"
                                                sx={{
                                                    position: "relative",
                                                    width: 96,
                                                    aspectRatio: "16/9",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img
                                                    src={item.thumnail || "/static/images/workout1.webp"}
                                                    alt={item.title || "Workout"}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block",
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        bottom: 4,
                                                        right: 4,
                                                        bgcolor: "rgba(0,0,0,0.7)",
                                                        color: "#fff",
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        px: 0.75,
                                                        py: 0.25,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    {formatDurationShort(item.durationInMins, item.duration)}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <b>{item.title || "N/A"}</b>
                                        </TableCell>

                                        <TableCell>{item.refCode || "N/A"}</TableCell>

                                        <TableCell>{formatDuration(item.durationInMins, item.duration)}</TableCell>

                                        <TableCell>{formatDifficulty(item.difficultyLevel)}</TableCell>

                                        <TableCell>{getMainProgramTitle(item)}</TableCell>

                                        <TableCell>
                                            <SwitchToggle
                                                checked={item.isPublished}
                                                onChange={handleTogglePublish(item)}
                                                disabled={isUpdatingStatus}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <div className="table_actions v2">
                                                <IconButton
                                                    onClick={() => navigate("view", { state: { workoutId: item._id } })}
                                                >
                                                    <img src="/static/images/eye_icon.svg" alt="View" />
                                                </IconButton>

                                                <IconButton
                                                    onClick={() =>
                                                        navigate(`add?workoutId=${encodeURIComponent(item._id)}`)
                                                    }
                                                >
                                                    <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                                </IconButton>

                                                <IconButton
                                                    title="Duplicate"
                                                    onClick={() =>
                                                        navigate(`add?duplicateFrom=${encodeURIComponent(item._id)}`)
                                                    }
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>

                                                <IconButton
                                                    onClick={() => {
                                                        setWorkoutToDelete(item);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                    disabled={isDeleting}
                                                >
                                                    <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                                </IconButton>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>

                {!isLoading && !workoutsError && totalCount > 0 && (
                    <Pagination
                        module={workouts}
                        page={page}
                        onPageChange={(newPage) => {
                            setPage(newPage);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        totalPages={Math.ceil(totalCount / limit)}
                        totalCount={totalCount}
                        setPage={setPage}
                        limit={limit}
                    />
                )}

                <ConfirmationDialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setWorkoutToDelete(null);
                    }}
                    onConfirm={() => {
                        if (workoutToDelete?._id) {
                            deleteWorkout(workoutToDelete._id);
                        }
                    }}
                    title="Delete Workout"
                    message="Are you sure you want to delete this workout? This action cannot be undone."
                    confirmText="Delete"
                    cancelText="Cancel"
                    confirmButtonColor="error"
                    isLoading={isDeleting}
                    loadingText="Deleting..."
                />
            </div>
        </>
    );
};

export default ManageWorkout;
